import { MarkFeedbackType, TutorStateAtomType } from 'src/types/generic';
import { testIds } from 'src/app/constants';
import { IConversationMessage } from 'src/types/serviceResponseTypes';
import Feedback from 'src/app/components/Feedback/Feedback';
import SingleInput from './SingleInput/SingleInput';
import MultipleInput from './MultipleInput/MultipleInput';
import styles from './TutorChatBubble.module.scss';
import { TUserInputType } from 'src/app/hooks/network/useSendConversation';

import { checkExerciseAtom, explanationPinAtom, pinAtom } from 'src/app/state/atoms';
import { useRecoilState } from 'recoil';
import PinButton from '../PinButton/PinButton';
import { t } from 'i18next';

type TutorChatBubbleType = {
  conversation: IConversationMessage;
  isIntroMessage?: boolean;
  isFirstQuestion?: boolean;
  tutorState?: TutorStateAtomType;
  isLastItem?: boolean;
  itemIndex: number;
  isSecondLastItem?: boolean;
  onOptionSelect?: (optionKey: string, userInputType?: TUserInputType) => void;
  markFeedback?: (options: MarkFeedbackType) => void;
  onReleaseNextQuestionGate?: () => void;
  hideFromScreenReader?: boolean;
  shouldFocusExplanation?: boolean;
};

function TutorChatBubble(props: TutorChatBubbleType) {
  const {
    conversation,
    onOptionSelect,
    isIntroMessage,
    markFeedback,
    tutorState,
    isLastItem,
    isSecondLastItem,
    onReleaseNextQuestionGate,
    hideFromScreenReader,
    shouldFocusExplanation,
    itemIndex,
  } = props;
  const [pinState] = useRecoilState(pinAtom);
  const [exPinList] = useRecoilState(explanationPinAtom);
  const [checkExercise] = useRecoilState(checkExerciseAtom);

  const isHeaderPinned = () => {
    for (const pin of pinState.pins ?? []) {
      if (pin.pinId === 'pin-header') {
        return true;
      }
    }
    return false;
  };

  const isExPinned = () => {
    for (const pin of pinState.pins ?? []) {
      if (exPinList.includes(pin.pinId)) {
        return true;
      }
    }
    return false;
  };

  return (
    <div
      id={isIntroMessage ? testIds.pinnedConversation : undefined}
      aria-hidden={hideFromScreenReader || undefined}
      className={
        isIntroMessage
          ? isHeaderPinned()
            ? styles.introMessageContainerPinned
            : styles.introMessageContainer
          : conversation.content_type === 'single_input'
          ? styles.singleInputBubble
          : styles.chatBubbleContainer
      }
    >
      <span className='sr-only'>{t('MESSAGE_FROM_TUTOR')}&nbsp;</span>
      {conversation.content_type === 'single_input' ? (
        <SingleInput
          isIntroMessage={isIntroMessage}
          content={conversation.content}
          isLastItem={isLastItem}
        />
      ) : (
        <div className='w-full'>
          <MultipleInput
            conversation={conversation}
            itemIndex={itemIndex}
            onInputSelect={onOptionSelect}
            isSMEMode={tutorState?.isSMEMode}
            isHistoryMode={tutorState?.isHistoryMode}
            isExplanationPinned={isExPinned()}
            isLastItem={isLastItem}
            isSecondLastItem={isSecondLastItem}
            shouldFocusExplanation={shouldFocusExplanation}
            onReleaseNextQuestionGate={onReleaseNextQuestionGate}
          />
          {(conversation.explanation || tutorState?.isSMEMode) && (
            <Feedback
              markFeedback={markFeedback}
              feedback={conversation.feedback}
              skipPositiveModal={Boolean(tutorState?.skipPositiveModal)}
              skipNegativeModal={Boolean(tutorState?.skipNegativeModal)}
              explanation={conversation.explanation ?? ''}
              isHistoryMode={tutorState?.isHistoryMode}
              isCorrect={conversation.is_correct}
              onReleaseNextQuestionGate={onReleaseNextQuestionGate}
              suppressForAnnouncement={shouldFocusExplanation}
            />
          )}
        </div>
      )}
      {isIntroMessage ? (
        <div className='self-center px-space-3' aria-hidden='true'>
          {checkExercise.data.enable_pinning &&
            !tutorState?.isSMEMode &&
            !tutorState?.isHistoryMode && (
              <div>
                <PinButton
                  label={conversation.content}
                  group={conversation.id}
                  providedPinId='pin-header'
                />
              </div>
            )}
        </div>
      ) : null}
    </div>
  );
}

export default TutorChatBubble;
